/**
 * RequestList Component
 * Displays a paginated list of LLM requests with expandable details
 */

import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  Collapse,
  Box,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Info as InfoIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Cached as CacheIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { LLMRequest, LLMRequestFilters } from '../../services/llm-monitoring';
import RequestDetails from './RequestDetails';

interface RequestListProps {
  requests: LLMRequest[];
  onRefresh: () => void;
  filters: LLMRequestFilters;
  onFilterChange: (key: keyof LLMRequestFilters, value: any) => void;
}

const RequestList: React.FC<RequestListProps> = ({
  requests,
  onRefresh,
  filters,
  onFilterChange
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [selectedRequest, setSelectedRequest] = useState<LLMRequest | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const handleRowExpand = (id: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const handleViewDetails = (request: LLMRequest) => {
    setSelectedRequest(request);
    setDetailsOpen(true);
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    onFilterChange('offset', newPage * (filters.limit || 100));
    onRefresh();
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange('limit', parseInt(event.target.value, 10));
    onFilterChange('offset', 0);
    onRefresh();
  };

  const getModelShortName = (model: string) => {
    if (model.includes('phi-4')) return 'Phi-4';
    if (model.includes('claude')) return 'Claude';
    if (model.includes('gpt')) return 'GPT';
    return model.split('/').pop() || model;
  };

  const getRequestTypeColor = (type: string) => {
    switch (type) {
      case 'routing':
        return 'primary';
      case 'tool_call':
        return 'secondary';
      case 'general':
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={40} />
              <TableCell>Timestamp</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Model</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Tool</TableCell>
              <TableCell align="right">Tokens</TableCell>
              <TableCell align="right">Cost</TableCell>
              <TableCell align="right">Latency</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.map((request) => (
              <React.Fragment key={request.id}>
                <TableRow hover>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleRowExpand(request.id)}
                    >
                      {expandedRows.has(request.id) ? <CollapseIcon /> : <ExpandIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    {format(new Date(request.timestamp), 'MMM dd HH:mm:ss')}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{request.user_id}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getModelShortName(request.model)}
                      size="small"
                      variant={request.fallback_used ? 'outlined' : 'filled'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={request.request_type}
                      size="small"
                      color={getRequestTypeColor(request.request_type)}
                    />
                  </TableCell>
                  <TableCell>
                    {request.tool_name ? (
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {request.tool_name}
                      </Typography>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Box>
                      <Typography variant="body2">{request.total_tokens}</Typography>
                      {request.cache_hit && (
                        <Tooltip title="Cache hit">
                          <CacheIcon fontSize="small" color="success" />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      ${Number(request.cost).toFixed(4)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {request.latency_ms}ms
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {request.success ? (
                      <Tooltip title="Success">
                        <SuccessIcon color="success" fontSize="small" />
                      </Tooltip>
                    ) : (
                      <Tooltip title={request.error_message || 'Failed'}>
                        <ErrorIcon color="error" fontSize="small" />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View details">
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(request)}
                      >
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={11} sx={{ py: 0 }}>
                    <Collapse in={expandedRows.has(request.id)} timeout="auto" unmountOnExit>
                      <Box sx={{ p: 2, bgcolor: 'background.default' }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" gutterBottom>
                              Request Preview
                            </Typography>
                            <Paper sx={{ p: 1, bgcolor: 'grey.50', maxHeight: 200, overflow: 'auto' }}>
                              <Typography variant="caption" component="pre" sx={{ fontFamily: 'monospace' }}>
                                {JSON.stringify(request.request_messages, null, 2).substring(0, 500)}...
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" gutterBottom>
                              Response Preview
                            </Typography>
                            <Paper sx={{ p: 1, bgcolor: 'grey.50', maxHeight: 200, overflow: 'auto' }}>
                              <Typography variant="caption" component="pre" sx={{ fontFamily: 'monospace' }}>
                                {JSON.stringify(request.response_content, null, 2).substring(0, 500)}...
                              </Typography>
                            </Paper>
                          </Grid>
                          {request.reasoning && (
                            <Grid item xs={12}>
                              <Typography variant="subtitle2" gutterBottom>
                                Reasoning
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {request.reasoning}
                              </Typography>
                            </Grid>
                          )}
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                              <Chip
                                label={`Prompt: ${request.prompt_tokens}`}
                                size="small"
                                variant="outlined"
                              />
                              <Chip
                                label={`Completion: ${request.completion_tokens}`}
                                size="small"
                                variant="outlined"
                              />
                              {request.confidence && (
                                <Chip
                                  label={`Confidence: ${(Number(request.confidence) * 100).toFixed(0)}%`}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                              {request.finish_reason && (
                                <Chip
                                  label={`Finish: ${request.finish_reason}`}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[50, 100, 200]}
          component="div"
          count={-1} // Unknown total count
          rowsPerPage={filters.limit || 100}
          page={Math.floor((filters.offset || 0) / (filters.limit || 100))}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </TableContainer>

      {/* Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Request Details
          {selectedRequest && (
            <Typography variant="caption" sx={{ ml: 2 }}>
              ID: {selectedRequest.id}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <RequestDetails request={selectedRequest} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RequestList;